import { computed, inject, Injectable, signal } from '@angular/core';
import { ManagementService } from '@thiememeulenhoff/ng-api-gateway';
import { BehaviorSubject, map, tap } from 'rxjs';
import {
  BulkDeleteCollection,
  ManagementMergeConflictsResponse,
  ManagementResult,
  ManagementStatistic,
  Result,
} from '@thiememeulenhoff/types';

@Injectable({
  providedIn: 'root',
})
export class ManagementFacade {
  readonly #managementService = inject(ManagementService);

  readonly #managementResultsById = signal<Record<string, ManagementResult>>(
    {},
  );

  readonly #managementStatisticsByActorId = signal<
    Record<string, ManagementStatistic>
  >({});

  readonly #managementMergeConflictsSubject =
    new BehaviorSubject<ManagementMergeConflictsResponse | null>(null);

  readonly managementMergeConflicts$ =
    this.#managementMergeConflictsSubject.asObservable();

  readonly managementResults = this.#managementResultsById.asReadonly();

  readonly managementStatistics =
    this.#managementStatisticsByActorId.asReadonly();

  readonly managementResult = (actorId: string, arrangementCode: string) =>
    computed(() => {
      return this.managementResults()[`${actorId}_${arrangementCode}`];
    });

  readonly managementStatistic = (actorId: string) =>
    computed(() => {
      return this.managementStatistics()[actorId];
    });

  fetchManagementResult(actorId: string, arrangementCode: string) {
    return this.#managementService
      .fetchResultForArrangementByActor(actorId, arrangementCode)
      .pipe(
        map((managementResult: ManagementResult | Array<Result>) => {
          const normalizedResults = Array.isArray(managementResult)
            ? managementResult
            : (managementResult.results ??
              (managementResult as unknown as { data?: Array<Result> }).data ??
              []);

          return {
            ...(Array.isArray(managementResult) ? {} : managementResult),
            results: normalizedResults,
            identifier: `${actorId}_${arrangementCode}`,
          } as ManagementResult;
        }),
        tap((managementResult: ManagementResult) => {
          this.#upsertManagementResult(managementResult);
        }),
      );
  }

  #upsertManagementResult(managementResult: ManagementResult) {
    this.#managementResultsById.update((currentResults) => ({
      ...currentResults,
      [managementResult.identifier]: managementResult,
    }));
  }

  #upsertManagementStatistic(managementStatistic: ManagementStatistic) {
    this.#managementStatisticsByActorId.update((currentStatistics) => ({
      ...currentStatistics,
      [managementStatistic.identifier]: managementStatistic,
    }));
  }

  fetchManagementStatistics(actorId: string) {
    return this.#managementService.fetchStatisticsByActor(actorId).pipe(
      map(
        (
          managementStatistic:
            ManagementStatistic | ManagementStatistic['statistics'],
        ) => {
          const normalizedStatistics = Array.isArray(managementStatistic)
            ? managementStatistic
            : (managementStatistic.statistics ?? []);

          return {
            ...(Array.isArray(managementStatistic) ? {} : managementStatistic),
            statistics: normalizedStatistics,
            identifier: actorId,
          } as ManagementStatistic;
        },
      ),
      tap((managementStatistic: ManagementStatistic) => {
        this.#upsertManagementStatistic(managementStatistic);
      }),
    );
  }

  mergeActorResults(
    formerActorId: string,
    targetActorId: string,
    arrangementCode?: string,
  ) {
    return this.#managementService.mergeActorResults(
      formerActorId,
      targetActorId,
      arrangementCode,
    );
  }

  fetchMergeConflicts(
    formerActorId: string,
    targetActorId: string,
    arrangementCode?: string,
  ) {
    return this.#managementService
      .fetchMergeConflicts(formerActorId, targetActorId, arrangementCode)
      .pipe(
        tap((response: ManagementMergeConflictsResponse) => {
          this.#managementMergeConflictsSubject.next(response);
        }),
      );
  }

  deleteResult(collection: BulkDeleteCollection) {
    return this.#managementService.bulkDeleteResult(collection);
  }
}
